import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import type {
	DatabaseShareProgress,
	ShareContentType,
	WhiteboardShareProgress,
} from '../share-criteria-types.ts';
import { isShareProgressForType } from '../utils/isShareProgressForType.ts';

import {
	type NotifyFlagToShareProgress,
	useFlagToShare,
	type UseFlagToShareResult,
} from './useFlagToShare.ts';

type CanvasShareContentType = Exclude<ShareContentType, 'live-doc'>;
type CanvasShareProgress = DatabaseShareProgress | WhiteboardShareProgress;

type ActiveCanvasBaseline = {
	contentId: string;
	type: CanvasShareContentType;
	progress: CanvasShareProgress;
};

const getCanvasProgressDelta = (
	progress: CanvasShareProgress,
	baseline: CanvasShareProgress,
): CanvasShareProgress => {
	if (progress.type === 'whiteboard') {
		return {
			type: 'whiteboard',
			elements:
				baseline.type === 'whiteboard' ? Math.max(progress.elements - baseline.elements, 0) : 0,
		};
	}

	return {
		type: 'database',
		rows: baseline.type === 'database' ? Math.max(progress.rows - baseline.rows, 0) : 0,
		columns: baseline.type === 'database' ? Math.max(progress.columns - baseline.columns, 0) : 0,
		filledCells:
			baseline.type === 'database' ? Math.max(progress.filledCells - baseline.filledCells, 0) : 0,
	};
};

export type NotifyCanvasFlagToShareProgress = NotifyFlagToShareProgress;
export type UseCanvasFlagToShareResult = UseFlagToShareResult;

export type UseCanvasFlagToShareParams = {
	type: CanvasShareContentType;
	contentId: string;
};

/** Adapts absolute canvas totals into active-session deltas for the shared lifecycle. */
export function useCanvasFlagToShare({
	type,
	contentId,
}: UseCanvasFlagToShareParams): UseCanvasFlagToShareResult {
	const { shouldCollectProgress, notifyProgress: notifyNormalizedProgress } = useFlagToShare({
		type,
		contentId,
	});
	const session = useMemo(() => ({ contentId, type }), [contentId, type]);
	const activeBaselineRef = useRef<ActiveCanvasBaseline | null>(null);
	const latestNotifierRef = useRef(notifyNormalizedProgress);
	const activeSessionRef = useRef<object | null>(null);

	useLayoutEffect(() => {
		activeBaselineRef.current = null;
		activeSessionRef.current = session;

		return () => {
			if (activeSessionRef.current === session) {
				activeSessionRef.current = null;
			}
		};
	}, [session]);

	useLayoutEffect(() => {
		latestNotifierRef.current = notifyNormalizedProgress;
	}, [notifyNormalizedProgress]);

	const notifyProgress = useCallback<NotifyFlagToShareProgress>(
		(progress) => {
			if (activeSessionRef.current !== session) {
				return;
			}

			if (!isShareProgressForType(type, progress)) {
				latestNotifierRef.current(progress);
				return;
			}

			const activeBaseline = activeBaselineRef.current;
			if (
				!activeBaseline ||
				activeBaseline.contentId !== contentId ||
				activeBaseline.type !== type
			) {
				activeBaselineRef.current = { contentId, type, progress };
				latestNotifierRef.current(getCanvasProgressDelta(progress, progress));
				return;
			}
			latestNotifierRef.current(getCanvasProgressDelta(progress, activeBaseline.progress));
		},
		[contentId, session, type],
	);

	return { shouldCollectProgress, notifyProgress };
}
